import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { shouldResetCountyField } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import dayjs from 'dayjs';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { TODAY_IN_DAYS, YESTERDAY_IN_DAYS } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { DEFAULT_COUNTRY, HousingType } from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  selectNonUSLocationOptions,
  selectSortedCountiesByStates,
} from 'store/loanApplications/selectors';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';

import { getAddressConfig } from './Address.config';
import {
  previousAddressContainerStyles,
  previousAddressFormControlsStyles,
} from './Address.styles';
import { filterPreviousAddressForm } from './Address.utils';

const PreviousAddressForm = ({
  previousAddressData,
  isLoading,
  isRefinanceFlow,
  cancelForm,
  pendingChanges,
  isLiveTogetherForTwoYears,
  deletePreviousAddress,
  saveForm,
}) => {
  const dispatch = useDispatch();

  const isMilitaryAddressAllowed = useFeatureToggle(FeatureFlag.MilitaryAddressToStates);

  const counties = useSelector(selectSortedCountiesByStates);
  const nonUSCountries = useSelector(selectNonUSLocationOptions);

  const observableFieldsNames = Object.keys(previousAddressData);

  const { initialFormValues, schema, fieldsConfig } = useMemo(() => {
    const addressState = previousAddressData.state;
    const addressCounties = addressState ? counties[addressState] : counties.ALL_COUNTIES;

    const initialConfig = getAddressConfig({
      section: 'previous',
      addressCounties,
      ownershipType: previousAddressData.housingType,
      hasRelativesPartRent: previousAddressData.hasRelativesPartRent,
      isRefinanceFlow,
      isLiveTogetherForTwoYears,
      addressIsOutsideUsa: previousAddressData.isOutsideUsa,
      nonUSCountries,
      isMilitaryAddressAllowed,
      isPreviousResidence: true,
      endOfResidence: previousAddressData.endOfResidence,
      startOfResidence: previousAddressData.startOfResidence,
    });

    return getConfigInfo({
      applicationData: previousAddressData,
      config: initialConfig,
      isNestedForm: true,
    });
  }, [
    counties,
    isLiveTogetherForTwoYears,
    isMilitaryAddressAllowed,
    isRefinanceFlow,
    nonUSCountries,
    previousAddressData,
  ]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
    setValidationErrors,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const addressIsOutsideUsa = form.isOutsideUsa;
  const prevAddressIsOutsideUsa = useRef(addressIsOutsideUsa);
  const endOfResidence = form.endOfResidence?.toString();
  const startOfResidence = form.startOfResidence?.toString();

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = filterPreviousAddressForm(
      mergePendingChanges(pendingChanges, formPendingChanges),
    );

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  const formGeneratorConfig = useMemo(() => {
    const addressState = form.state?.value;
    const addressCounties = addressState ? counties[addressState] : counties.ALL_COUNTIES;

    return getAddressConfig({
      section: 'previous',
      addressCounties,
      ownershipType: form.housingType?.value,
      hasRelativesPartRent: form.hasRelativesPartRent,
      isRefinanceFlow,
      isLiveTogetherForTwoYears,
      addressIsOutsideUsa: form.isOutsideUsa,
      nonUSCountries,
      isMilitaryAddressAllowed,
      isPreviousResidence: true,
      endOfResidence,
      startOfResidence,
    });
  }, [
    counties,
    endOfResidence,
    form.hasRelativesPartRent,
    form.housingType?.value,
    form.isOutsideUsa,
    startOfResidence,
    form.state?.value,
    isLiveTogetherForTwoYears,
    isMilitaryAddressAllowed,
    isRefinanceFlow,
    nonUSCountries,
  ]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    const { schema } = getConfigInfo({ applicationData: form, config: formGeneratorConfig });

    setSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.housingType?.value, form.hasRelativesPartRent, addressIsOutsideUsa]);

  useEffect(() => {
    if (form.startOfResidence >= TODAY_IN_DAYS) {
      setInForm('startOfResidence', YESTERDAY_IN_DAYS, false);
    }

    if (form.endOfResidence > TODAY_IN_DAYS) {
      setInForm('endOfResidence', TODAY_IN_DAYS, false);
    }

    if (
      form.startOfResidence &&
      form.endOfResidence &&
      form.endOfResidence <= form.startOfResidence
    ) {
      setInForm('endOfResidence', dayjs(form.startOfResidence).add(1, 'day'), false);
    }
  }, [setInForm, form.startOfResidence, form.endOfResidence]);

  useEffect(() => {
    if (form.housingType?.value === HousingType.OWN) {
      setMultipleInForm(
        {
          monthlyRentAmount: null,
          hasRelativesPartRent: false,
          monthlyRelativesPartRentAmount: null,
        },
        false,
      );
    }

    if (form.housingType?.value === HousingType.RENT) {
      setMultipleInForm(
        { hasRelativesPartRent: null, monthlyRelativesPartRentAmount: null },
        false,
      );
    }

    if (form.housingType?.value === HousingType.LIVING_WITH_RELATIVE) {
      setInForm('monthlyRentAmount', null, false);
    }
  }, [setInForm, setMultipleInForm, form.housingType?.value]);

  const resetAddressIsOutsideUSARelatedFields = useCallback(
    ({ addressIsOutsideUsa }) => {
      if (
        _.isBoolean(prevAddressIsOutsideUsa.current) &&
        prevAddressIsOutsideUsa.current !== addressIsOutsideUsa
      ) {
        setMultipleInForm({ state: '', country: '', zip: '', county: '' }, false);
      }

      if (!addressIsOutsideUsa) {
        setInForm('country', DEFAULT_COUNTRY, false);
        setValidationErrors(prevState => ({
          ...prevState,
          country: '',
        }));
      }

      prevAddressIsOutsideUsa.current = addressIsOutsideUsa;
    },
    [setInForm, setMultipleInForm, setValidationErrors],
  );

  useEffect(() => {
    resetAddressIsOutsideUSARelatedFields({
      addressIsOutsideUsa,
    });
  }, [addressIsOutsideUsa, resetAddressIsOutsideUSARelatedFields]);

  useEffect(() => {
    const shouldResetCounty = shouldResetCountyField({
      counties,
      formState: form.state?.value,
      formCounty: form.county?.value,
    });

    if (!shouldResetCounty) {
      return;
    }

    setInForm('county', null, false);
  }, [counties, form.county?.value, form.state?.value, setInForm]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: previousAddressData });
    },
    [form, validateForm, saveForm, previousAddressData],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(previousAddressData);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(previousAddressData);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, previousAddressData]);

  const onClickRemove = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Remove Previous Address',
        description: 'Are you sure you want to remove this previous address?',
        onAccept: () => {
          deletePreviousAddress(previousAddressData.id);
        },
      }),
    );
  }, [deletePreviousAddress, dispatch, previousAddressData.id]);

  return (
    <form
      noValidate
      data-ischanged={isFormChanged}
      data-testid={BorrowersTestIds.PREVIOUS_ADDRESS_FORM}
      onSubmit={onSubmit}
    >
      <Box sx={previousAddressContainerStyles}>
        <FormGenerator
          isNestedForm
          config={formGeneratorConfig}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.BORROWER_ADDRESS}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={4}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box>
          <Button
            data-testid={BorrowersTestIds.PREVIOUS_ADDRESS_FORM_REMOVE_BUTTON}
            size="small"
            startIcon={<DeleteIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={onClickRemove}
          >
            Remove Address
          </Button>
        </Box>
        <Box sx={previousAddressFormControlsStyles}>
          <Button
            data-testid={BorrowersTestIds.PREVIOUS_ADDRESS_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={BorrowersTestIds.PREVIOUS_ADDRESS_FORM_ADD_BUTTON}
            disabled={!isFormChanged}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

PreviousAddressForm.propTypes = {
  deletePreviousAddress: PropTypes.func.isRequired,
  isLiveTogetherForTwoYears: PropTypes.bool.isRequired,
  isRefinanceFlow: PropTypes.bool.isRequired,
  previousAddressData: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  ...WithLoadingPropTypes,
};

export default memo(PreviousAddressForm);
